'use client';

import { useEffect, useRef, useState, ReactNode } from 'react';

// ============================================
// SPARKLE BURST - Confetti effect on click
// ============================================
interface SparkleParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

export function SparkleBurst({ children, onClick, disabled = false }: { 
  children: ReactNode; 
  onClick?: () => void;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<SparkleParticle[]>([]);
  const animationRef = useRef<number | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference only on client
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
  }, []);

  const createBurst = (clientX: number, clientY: number) => {
    if (prefersReducedMotion || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const colors = ['#C5B4FF', '#B5F2E0', '#FFCFE1', '#BFDFFF', '#F8C8A0'];
    const particleCount = 12;

    for (let i = 0; i < particleCount; i++) {
      const angle = (Math.PI * 2 * i) / particleCount;
      const velocity = 2 + Math.random() * 3;
      
      particlesRef.current.push({
        x,
        y,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        life: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    if (!canvasRef.current && containerRef.current) {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '9999';
      canvas.width = rect.width;
      canvas.height = rect.height;
      containerRef.current.appendChild(canvas);
      canvasRef.current = canvas;
    }

    if (!animationRef.current) {
      animateParticles();
    }
  };

  const animateParticles = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current = particlesRef.current.filter(p => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.1; // gravity
      p.life -= 0.016; // ~60fps

      if (p.life > 0) {
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fill();
        return true;
      }
      return false;
    });

    if (particlesRef.current.length > 0) {
      animationRef.current = requestAnimationFrame(animateParticles);
    } else {
      animationRef.current = null;
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
      canvasRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (canvasRef.current && canvasRef.current.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
      }
    };
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      createBurst(e.clientX, e.clientY);
      onClick?.();
    }
  };

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      style={{ position: 'relative', display: 'inline-block', cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {children}
    </div>
  );
}

// ============================================
// SOFT GLOW - Radial background glow wrapper
// ============================================
export function SoftGlow({ children, color = '#C9B6E4', intensity = 0.3 }: { 
  children: ReactNode; 
  color?: string;
  intensity?: number;
}) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference only on client
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    // Listen for changes
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <div style={{ 
      position: 'relative', 
      isolation: 'isolate',
    }}>
      {!prefersReducedMotion && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120%',
            height: '120%',
            background: `radial-gradient(circle, ${color}${Math.round(intensity * 255).toString(16).padStart(2, '0')} 0%, transparent 70%)`,
            filter: 'blur(40px)',
            pointerEvents: 'none',
            zIndex: -1,
          }}
        />
      )}
      {children}
    </div>
  );
}

// ============================================
// SHIMMER BORDER - Animated gradient border
// ============================================
export function ShimmerBorder({ children, className = '' }: { 
  children: ReactNode; 
  className?: string;
}) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check for reduced motion preference only on client
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
  }, []);

  return (
    <div 
      className={className}
      style={{ 
        position: 'relative',
        borderRadius: 'var(--r-2xl, 28px)',
        padding: '3px',
        background: prefersReducedMotion 
          ? 'linear-gradient(135deg, #C5B4FF, #B5F2E0)'
          : 'linear-gradient(90deg, #C5B4FF, #B5F2E0, #FFCFE1, #C5B4FF)',
        backgroundSize: '300% 100%',
        animation: prefersReducedMotion ? 'none' : 'shimmerBorder 3s linear infinite',
      }}
    >
      <div style={{ 
        background: 'white', 
        borderRadius: 'calc(var(--r-2xl, 28px) - 3px)',
        width: '100%',
        height: '100%',
      }}>
        {children}
      </div>
      <style jsx>{`
        @keyframes shimmerBorder {
          0% { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
      `}</style>
    </div>
  );
}

